import * as React from 'react';
// form
import { useFormContext, Controller } from 'react-hook-form';
import { cn } from '../../lib/utils';

/* ============================================================================
   Campo de texto do visual novo (shadcn + Tailwind). Sem MUI.

   O import de react-input-mask que existia aqui era MORTO - nenhum <InputMask>
   neste arquivo. Como este componente e usado em 21 telas (inclusive o login),
   esse import sozinho arrastava uma lib abandonada desde 2019 para o bundle de
   todo mundo e travava a subida para o React 19. A mascara de verdade mora em
   RHFTextMask.jsx.

   A ASSINATURA PUBLICA NAO MUDOU: `name`, `label`, `size`, `variant`,
   `slotProps`, `sx`, `fullWidth`, `disabled`, `type`, `id`, `placeholder` e
   `required` continuam aceitos. As que eram apenas estetica do MUI (`size`,
   `variant`, `fullWidth`, `slotProps`, `InputProps`...) sao ACEITAS E
   DESCARTADAS aqui dentro - o ponto de descartar explicitamente e que elas NAO
   podem cair no `...resto` e virar atributo invalido no <input> (`fullWidth`,
   `slotProps` no DOM geram aviso do React e sujam o HTML).

   DOIS DEFEITOS CORRIGIDOS:

   1. `value` do react-hook-form chega `undefined`/`null` quando o campo ainda nao
      foi preenchido (ou quando o `reset` traz um registro do banco com coluna
      nula). Um <input> com value undefined vira NAO CONTROLADO no meio do ciclo
      de vida: o React avisa "changing an uncontrolled input to be controlled" e o
      primeiro caractere digitado pode ser descartado. Normalizado para ''.

   2. `disabled` chegava como VALOR DE DADO, nao booleano - as telas antigas usam
      `disabled={oldEntity?.descricao}` (uma string) e `disabled={oldEntity.nome}`.
      String vazia/undefined funcionava por acidente, mas `disabled="ATIVO"` no MUI
      passava direto e no DOM nativo tambem - o problema e que o React reclama de
      tipo. Convertido com Boolean().
   ============================================================================ */

// Fator de espacamento do MUI: sx={{ mt: 2 }} valia 16px.
const ESPACO = 8;

/**
 * Traduz o pouco do `sx` do MUI que da para traduzir sem adivinhar, e IGNORA o
 * resto em silencio. Existe para os chamadores antigos nao precisarem mudar.
 *
 * `mt/mb/ml/mr/mx/my` seguem o fator de espacamento (n * 8px), como no MUI.
 * `width/height` NAO seguem: no MUI eles sao valor de CSS cru (`width: 200` =
 * 200px), e multiplicar por 8 transformaria 200 em 1600px - seria "traduzir"
 * errado de proposito.
 */
export function sxParaEstilo(sx) {
  if (!sx || typeof sx !== 'object' || Array.isArray(sx)) return undefined;

  const estilo = {};
  const espaco = (v) => (typeof v === 'number' ? `${v * ESPACO}px` : v);
  const cru = (v) => (typeof v === 'number' ? `${v}px` : v);

  if (sx.mt !== undefined) estilo.marginTop = espaco(sx.mt);
  if (sx.mb !== undefined) estilo.marginBottom = espaco(sx.mb);
  if (sx.ml !== undefined) estilo.marginLeft = espaco(sx.ml);
  if (sx.mr !== undefined) estilo.marginRight = espaco(sx.mr);
  if (sx.mx !== undefined) {
    estilo.marginLeft = espaco(sx.mx);
    estilo.marginRight = espaco(sx.mx);
  }
  if (sx.my !== undefined) {
    estilo.marginTop = espaco(sx.my);
    estilo.marginBottom = espaco(sx.my);
  }
  if (sx.width !== undefined) estilo.width = cru(sx.width);
  if (sx.height !== undefined) estilo.height = cru(sx.height);

  return Object.keys(estilo).length ? estilo : undefined;
}

// Classes da caixa de entrada. Iguais as de components/gps/Modal.tsx
// (`classesEntrada`) para todo campo do sistema ter a mesma altura e o mesmo
// foco. Repetido aqui, e nao importado, porque este arquivo e .jsx e carregado
// pelo login - nao vale acoplar o formulario de autenticacao ao modulo de modais.
export const CLASSES_ENTRADA =
  'h-9 w-full rounded-lg border border-line bg-surface px-3 text-[12.5px] text-ink outline-none transition-colors placeholder:text-faint focus:border-primary disabled:cursor-not-allowed disabled:bg-soft disabled:text-muted-foreground';

/** Rotulo + asterisco de obrigatorio, no padrao do visual novo. */
export function RotuloCampo({ htmlFor, children, obrigatorio }) {
  if (!children) return null;
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-ink">
      {children}
      {obrigatorio && <span className="ml-1 text-danger">*</span>}
    </label>
  );
}

/** Mensagem de erro da validacao. `id` para o input apontar via aria-describedby. */
export function ErroCampo({ id, children }) {
  if (!children) return null;
  return (
    <p id={id} className="mt-1 text-[12px] text-danger">
      {children}
    </p>
  );
}

// forwardRef porque as telas antigas envolvem estes campos em <Tooltip> do MUI,
// que CLONA o filho e injeta um ref nele. Componente de funcao sem forwardRef
// nessa posicao faz o React avisar e o tooltip nunca aparece.
// `@type {any}`: com `React.forwardRef` o TypeScript nao consegue inferir o tipo
// das props a partir do padrao de desestruturacao, e assume props vazias - o que
// faz TODO chamador .tsx quebrar em `name`, `fullWidth` etc. A versao anterior
// (MUI, `{ name, ...other }`) aceitava qualquer prop; isto preserva o mesmo
// contrato sem mexer nos chamadores.
/** @type {any} */
const RHFTextField = React.forwardRef(function RHFTextField(
  {
    name,
    label,
    required,
    id,
    sx,
    className,
    disabled,
    multiline,
    rows = 3,
    // Aceitas e descartadas: eram so aparencia do MUI. Ficam nomeadas para nao
    // vazarem para o DOM pelo `...resto`.
    fullWidth,
    size,
    variant,
    margin,
    color,
    slotProps,
    InputProps,
    InputLabelProps,
    inputProps,
    helperText,
    select,
    minRows,
    maxRows,
    ...resto
  },
  ref
) {
  const { control } = useFormContext();
  const idCampo = id || `campo-${name}`;
  const idErro = `${idCampo}-erro`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const comuns = {
          ...field,
          value: field.value ?? '',
          id: idCampo,
          disabled: Boolean(disabled),
          'aria-required': Boolean(required),
          'aria-invalid': Boolean(error),
          'aria-describedby': error ? idErro : undefined,
          ...resto,
        };

        return (
          <div ref={ref} className={cn('w-full', className)} style={sxParaEstilo(sx)}>
            <RotuloCampo htmlFor={idCampo} obrigatorio={required}>
              {label}
            </RotuloCampo>

            {multiline ? (
              <textarea
                {...comuns}
                rows={rows}
                className={cn(CLASSES_ENTRADA, 'h-auto py-2 leading-snug', error && 'border-danger')}
              />
            ) : (
              <input {...comuns} className={cn(CLASSES_ENTRADA, error && 'border-danger')} />
            )}

            <ErroCampo id={idErro}>{error?.message}</ErroCampo>
          </div>
        );
      }}
    />
  );
});

export default RHFTextField;
